import { Box, Modal } from '@mui/material';
import type { EventRoutesType } from '@source/graphql/autogenerate/schemas';
import CloseIcon from '@mui/icons-material/Close';
import { useState, useEffect } from 'react';
import MapGL, { Source, Layer } from 'react-map-gl';
import MapboxPolyline from '@mapbox/polyline';
import WebMercatorViewport from 'viewport-mercator-project';
import StartMarker from '../RouteMarkers/StartMarker';
import EndMarker from '../RouteMarkers/EndMarker';

import * as S from './styles';
import downloadGPX from '@source/utils/downloadGPX';
import { useToast } from '@source/recoil/useToast/hook';

type RouteCardModalProps = {
  currentRoute: EventRoutesType;
  isModalOpen: boolean;
  setIsModalOpen: (isModalOpen: boolean) => void;
};

const RouteCardModal: React.FC<RouteCardModalProps> = ({
  currentRoute,
  isModalOpen,
  setIsModalOpen
}): JSX.Element => {
  const [coordinates, setCoordinates] = useState<any>();
  const [viewport, setViewport] = useState({});
  const { handleToast } = useToast();

  const startCoordinate = coordinates?.coordinates[0];

  const lastCoordinate =
    coordinates?.coordinates[coordinates.coordinates.length - 1];

  const setBounds = () => {
    const bounds: [[number, number], [number, number]] = [
      currentRoute?.min_bounds as [number, number],
      currentRoute?.max_bounds as [number, number]
    ];

    const mercatorViewport = new WebMercatorViewport({
      width: 500,
      height: 300
    }).fitBounds(bounds);

    setViewport({ ...mercatorViewport });
  };

  useEffect(() => {
    if (currentRoute?.polyline) {
      setCoordinates(MapboxPolyline.toGeoJSON(currentRoute?.polyline));
      setBounds();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRoute?.polyline]);

  const layerStyle = {
    id: 'lineLayer',
    type: 'line',
    layout: {
      'line-join': 'round',
      'line-cap': 'round'
    },
    paint: {
      'line-color': '#0564FF',
      'line-width': 5
    }
  };

  return (
    <>
      {Object.keys(viewport).length > 0 && (
        <Modal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          sx={{
            display: 'grid',
            placeItems: 'center',
            overflowY: 'scroll'
          }}
        >
          <Box
            padding="1.5 4rem 4rem 4rem"
            sx={{
              background: '#fff',
              borderRadius: '0.8rem'
            }}
          >
            <S.TitleDiv>
              <S.Title>{currentRoute?.name}</S.Title>

              <S.ButtonDiv>
                <S.DownloadButton
                  onClick={() =>
                    downloadGPX({
                      routeId: currentRoute?.id,
                      routeName: currentRoute?.name,
                      handleToast: handleToast
                    })
                  }
                >
                  Exportar GPX
                </S.DownloadButton>
                <button
                  style={{ border: 'none', background: 'transparent' }}
                  onClick={() => setIsModalOpen(false)}
                >
                  <CloseIcon sx={{ fontSize: '2.2rem', color: '#BBC0C7' }} />
                </button>
              </S.ButtonDiv>
            </S.TitleDiv>
            <MapGL
              {...viewport}
              onMove={event => setViewport(event.viewState)}
              style={{ width: '90vw', height: '80vh' }}
              mapStyle="mapbox://styles/mapbox/outdoors-v11"
              mapboxAccessToken={process.env.NEXT_PUBLIC_MAPBOX_TOKEN}
              interactiveLayerIds={['lineLayer']}
            >
              <Source type="geojson" data={coordinates}>
                {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                {/* @ts-ignore: Unreachable code error */}
                <Layer {...layerStyle} />
                <StartMarker
                  latitude={startCoordinate[1]}
                  longitude={startCoordinate[0]}
                />
                <EndMarker coordinates={lastCoordinate} />
              </Source>
            </MapGL>
          </Box>
        </Modal>
      )}
    </>
  );
};

export default RouteCardModal;
